import React, { useEffect, useState } from 'react';
import { 
  Users, 
  TrendingUp,
  ArrowRight,
  Mail,
  Phone,
  User,
  MessageSquare,
  Send,
  CheckCircle2
} from 'lucide-react';
import { ContactForm } from '../types';
import { apiService } from '../services/api';
import Footer from '../components/Footer';
import PublicSiteHeader from '../components/PublicSiteHeader';

const Home: React.FC = () => {
  const [contactForm, setContactForm] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isStartingGroupCheckout, setIsStartingGroupCheckout] = useState(false);
  const [groupCheckoutError, setGroupCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    document.title =
      'VECTOR Athletic Performance System | Online Performance Coaching | Ireland';

    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionContent =
      'Stop training. Start performing. Online athletic performance coaching—Kildare, Dublin, Ireland. Training, nutrition, and recovery built around you.';

    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionContent);
      return;
    }

    const newMetaDescription = document.createElement('meta');
    newMetaDescription.setAttribute('name', 'description');
    newMetaDescription.setAttribute('content', descriptionContent);
    document.head.appendChild(newMetaDescription);
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await apiService.submitContactForm(contactForm);
      setSubmitStatus('success');
      setSubmitMessage("Thank you! We'll be in touch within 24 hours.");
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Sorry, there was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ContactForm, value: string) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-form');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartGroupCoaching = async () => {
    if (isStartingGroupCheckout) return;
    setGroupCheckoutError(null);
    setIsStartingGroupCheckout(true);
    try {
      const { checkout_url } = await apiService.createGroupCoachingCheckoutSession();
      if (!checkout_url) throw new Error('No checkout URL returned');
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Failed to start group coaching checkout:', error);
      setGroupCheckoutError('Sorry, we could not start checkout right now. Please try again.');
      setIsStartingGroupCheckout(false);
    }
  };

  return (
    <div className="min-h-screen">
      <PublicSiteHeader currentPage="home" />

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Stop training.</span>
                  <span className="block text-primary">Start performing.</span>
                </h1>
                <div className="mt-4 text-base text-gray-600 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0 space-y-3">
                  <p className="font-medium text-gray-800">
                    Vector Athletic System turns hard workers into athletes—built around you, not a generic plan.
                  </p>
                  <p>
                    You&apos;re already doing the hard part: the sessions, the early starts, the consistency. Effort
                    without the right system only gets you so far.
                  </p>
                  <p className="font-semibold text-gray-800">
                    Training. Nutrition. Recovery.{' '}
                    <span className="font-bold text-primary">Everything covered—nothing left to chance.</span>
                  </p>
                  <p className="text-sm text-gray-600 border-l-4 border-primary pl-3">
                    Online athletic performance coaching for athletes in{' '}
                    <span className="font-semibold text-gray-800">Kildare</span>,{' '}
                    <span className="font-semibold text-gray-800">Dublin</span>, and{' '}
                    <span className="font-semibold text-gray-800">across Ireland</span>.
                  </p>
                  <p className="font-medium text-gray-800">
                    Choose what suits you:
                  </p>
                  <ul className="list-none grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-600">
                    <li>
                      <span className="font-semibold text-gray-700">Online group coaching</span> — clear programme,
                      expert guidance, and a community that keeps you moving (without the 1:1 investment).
                    </li>
                    <li>
                      <span className="font-semibold text-gray-700">1:1 online coaching</span> — the full Vector system,
                      built entirely around you.
                    </li>
                  </ul>
                </div>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div className="rounded-md shadow">
                    <button
                      onClick={handleStartGroupCoaching}
                      disabled={isStartingGroupCheckout}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-primary-dark md:py-4 md:text-lg md:px-10"
                      aria-label="Start online group coaching checkout"
                    >
                      {isStartingGroupCheckout ? 'Redirecting to checkout...' : 'Join Group Coaching'}
                    </button>
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-3">
                    <button
                      type="button"
                      onClick={scrollToContact}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 md:py-4 md:text-lg md:px-10"
                      aria-label="Enquire about 1 to 1 online personal coaching"
                    >
                      Enquire about 1:1 coaching
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <img
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
            alt="Athlete training with focus and intent"
          />
        </div>
      </div>

      {/* Services Section */}
      <div className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Services</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Athletic performance coaching, delivered online
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Serious about the next level? You get structured training, nutrition that supports performance, recovery
              that keeps you in the game—and support that doesn&apos;t let standards slip.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Online Group Coaching (primary, "heat map" dominant) */}
            <section
              className="lg:col-span-4 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
              aria-label="Online Group Coaching service"
            >
              <div className="p-8 sm:p-10">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div className="min-w-[260px]">
                    {/* <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                      <Sparkles className="h-4 w-4" />
                      Best value (most popular)
                    </div> */}

                    <div className="mt-4 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-extrabold text-gray-900">Online Group Coaching</h3>
                        <p className="text-sm text-gray-500">Billed monthly • Gym or home based options</p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <p className="text-4xl font-extrabold text-gray-900">€50</p>
                      <p className="mt-1 text-sm text-gray-500">per month</p>
                    </div>

                    <p className="mt-6 text-gray-600">
                      Ideal if you want a structured monthly system, coaching in the room (virtually), and community
                      momentum—without the depth of full 1:1.
                    </p>
                  </div>

                  <div className="flex-1 min-w-[280px]">
                    <p className="text-sm font-semibold text-gray-900">What’s included</p>
                    <ul className="mt-4 space-y-3 text-gray-700">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Review of your goals and guidance from Oige on the best way to get there.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Monthly training programme (Gym or Home Based suited to your level).</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Nutritional Guidance with calories and macros for your goal.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Support and motivation in our vibrant support community with like minded others.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Weekly Q&amp;A’s on Zoom.</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <span>Recipe booklets.</span>
                      </li>
                    </ul>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={handleStartGroupCoaching}
                        disabled={isStartingGroupCheckout}
                        className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Start online group coaching checkout"
                      >
                        {isStartingGroupCheckout ? 'Redirecting to checkout...' : 'Join Group Coaching'}
                        <ArrowRight className="ml-2 -mr-1 w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        onClick={scrollToContact}
                        className="inline-flex items-center justify-center px-6 py-3 border border-primary/20 text-base font-medium rounded-md text-primary bg-primary/10 hover:bg-primary/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        aria-label="Ask a question about online group coaching"
                      >
                        Ask a question
                      </button>
                    </div>

                    {groupCheckoutError && (
                      <div className="mt-4 p-4 rounded-md bg-red-50 text-red-800 border border-red-200" role="alert">
                        {groupCheckoutError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* 1:1 Online Personal Coaching (enquiry) */}
            <section
              className="lg:col-span-8 bg-white rounded-2xl border border-primary/20 shadow-sm overflow-hidden"
              aria-label="1 to 1 Online Personal Coaching service"
            >
              <div className="p-8 sm:p-10">
                <p className="text-xs font-semibold tracking-wide text-primary uppercase">1:1 coaching</p>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-gray-700" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-extrabold text-gray-900 leading-snug">
                    For when &quot;busy&quot; isn&apos;t the problem—{' '}
                    <span className="text-primary">direction</span> is.
                  </h3>
                </div>

                <p className="mt-6 text-gray-600 text-sm leading-relaxed">
                  Some people don&apos;t need more motivation. They need a standard. Someone who sees the blind spots,
                  tightens the gaps, and won&apos;t let you quietly shrink your own expectations.
                </p>
                <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                  This isn&apos;t about chasing perfection. It&apos;s about{' '}
                  <span className="font-semibold text-gray-800">
                    finally feeling like your effort is building something you can trust
                  </span>
                  —in training, in nutrition, and when life tries to knock you off course.
                </p>

                <p className="mt-6 text-sm font-semibold text-gray-900">What shifts when you&apos;re inside the system</p>
                <ul className="mt-4 space-y-3 text-gray-700 text-sm">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-gray-900">A coach in your corner, every day</span> — direct support
                      when it matters, not just when it&apos;s scheduled.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-gray-900">Built for your body and your life</span> — training that
                      respects how you live, not a template.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-gray-900">Fuel you can sustain</span> — nutrition guidance and recipe
                      packs that feel doable.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-gray-900">People who expect more from themselves</span> — a
                      community that keeps the standard high.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary flex-shrink-0" aria-hidden="true" />
                    <span>
                      <span className="font-semibold text-gray-900">Merch you earn</span> — Vector Athletic System gear{' '}
                      <span className="font-semibold">only for athletes in the programme</span>. Not sold.{' '}
                      <span className="font-semibold text-primary">Earned.</span>
                    </span>
                  </li>
                </ul>

                <p className="mt-4 text-xs italic text-gray-500">
                  Limited spaces. We&apos;ll tell you honestly if 1:1 isn&apos;t the right fit.
                </p>

                <div className="mt-8">
                  <button
                    type="button"
                    onClick={scrollToContact}
                    className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
                    aria-label="Enquire about 1 to 1 online personal coaching"
                  >
                    Enquire now
                    <ArrowRight className="ml-2 -mr-1 w-5 h-5" />
                  </button>
                  <p className="mt-3 text-xs text-gray-500">
                    If that feeling sounds familiar—tell us below. Let&apos;s have a conversation.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div id="contact-form" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Get in touch</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Ready to see what you&apos;re capable of?
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Share your details—we&apos;ll be in touch <span className="font-semibold text-gray-700">within 24 hours</span>.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name *
                  </label>
                  <div className="mt-1 relative">
                    <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      id="name"
                      required
                      value={contactForm.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address *
                  </label>
                  <div className="mt-1 relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      id="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <div className="mt-1 relative">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    id="phone"
                    value={contactForm.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="+353 85 000 0000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message *
                </label>
                <div className="mt-1 relative">
                  <MessageSquare className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Tell us where you are stuck, what you are training for, and whether you want group or 1:1…"
                  />
                </div>
              </div>

              <p className="text-sm text-gray-500">
                No pressure. If we&apos;re not the right fit, we&apos;ll say so.
              </p>

              {submitStatus !== 'idle' && (
                <div className={`p-4 rounded-md ${
                  submitStatus === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Book a conversation
                      <Send className="ml-2 -mr-1 w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to find out what you&apos;re really capable of?</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-white/90">
            Online athletic performance coaching with VECTOR—training, nutrition, and recovery for athletes in Kildare,
            Dublin, and across Ireland.
          </p>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-md shadow">
              <button
                type="button"
                onClick={scrollToContact}
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary"
                aria-label="Scroll to contact form"
              >
                Fill in your details
                <ArrowRight className="ml-2 -mr-1 w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Home; 